import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { fetchCruiseTimes, fetchBoardingPoint } from '../utils/cruiseDetails';
import { fetchCruiseNames, cruiseLabel } from '../utils/cruiseNames';
import { getTicketPdfBase64 } from '../utils/ticketPDF';
import { to12hr } from './TicketPrint';
import { normalizeIndianMobile } from '../utils/phone';
import { MessageCircle, Check, X, AlertTriangle } from 'lucide-react';

const WA_GREEN = '#25D366';

// SendTicketModal — one-click "Send ticket via WhatsApp" for a booking.
// Builds the SAME ticket PDF used for download (as base64), lets staff pick the
// recipient(s) when it's a partner booking, normalizes each number, and calls
// the send-ticket edge function. The function uploads the PDF and calls Interakt
// once per number, returning a per-number result.

const recipientList = (booking) => {
  const list = [];
  if (booking.guest_phone && String(booking.guest_phone).trim()) {
    list.push({ key: 'guest', label: 'Guest', raw: booking.guest_phone });
  }
  if (booking.partner_phone && String(booking.partner_phone).trim()) {
    list.push({
      key: 'partner',
      label: booking.partner_name ? `Partner (${booking.partner_name})` : 'Partner',
      raw: booking.partner_phone,
    });
  }
  return list;
};

export default function SendTicketModal({ booking, reference, onClose }) {
  const available = recipientList(booking);
  const hasChoice = available.length > 1; // both guest + partner present

  // Default selection: single recipient is preselected; with a choice, none yet.
  const [choice, setChoice] = useState(hasChoice ? null : (available[0]?.key || null));
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null); // [{ label, number, success, error }]
  const [topError, setTopError] = useState('');

  // Which recipients the current choice resolves to.
  const chosen =
    choice === 'both' ? available
    : available.filter((r) => r.key === choice);

  const handleSend = async () => {
    if (!chosen.length) return;
    setSending(true);
    setTopError('');
    setResults(null);

    // Normalize each chosen number; keep invalid ones aside (do not send them).
    const normalized = chosen.map((r) => ({ ...r, norm: normalizeIndianMobile(r.raw) }));
    const valid = normalized.filter((r) => r.norm.valid);
    const invalid = normalized.filter((r) => !r.norm.valid);

    if (!valid.length) {
      // Nothing sendable — show why, don't call the function.
      setResults(invalid.map((r) => ({ label: r.label, number: r.raw, success: false, error: r.norm.reason })));
      setSending(false);
      return;
    }

    // Dedupe identical numbers (e.g. guest == partner) so we don't double-send.
    const seen = new Set();
    const uniqueNumbers = [];
    valid.forEach((r) => {
      if (!seen.has(r.norm.number)) { seen.add(r.norm.number); uniqueNumbers.push(r.norm.number); }
    });

    // Build the PDF (same as download) and, in parallel, source the five
    // template body values from the SAME shared lookup the printed ticket uses
    // (utils/cruiseDetails, for THIS booking's cruise date):
    //  - boarding point  (fallback 'Guwahati', as TicketPrint.js)
    //  - reporting time  via to12hr (TicketPrint.js)
    let pdf, boarding, times, names;
    try {
      [pdf, boarding, times, names] = await Promise.all([
        getTicketPdfBase64(booking, reference || null),
        fetchBoardingPoint({ date: booking.date }),
        fetchCruiseTimes({ cruiseName: booking.cruise_type, date: booking.date }),
        fetchCruiseNames({ cruiseName: booking.cruise_type }),
      ]);
    } catch (err) {
      console.error('Ticket build/source failed:', err);
      setTopError('Could not generate the ticket. Please try again.');
      setSending(false);
      return;
    }

    const boardingPoint = boarding?.boarding_point || 'Guwahati';
    const reportingTime = to12hr(times?.reporting_time);
    const bodyValues = [
      booking.booking_name || 'Guest', // name  — booking/guest name
      // cruise — SHORT display name: every WhatsApp template variable takes the
      // short name (utils/cruiseNames); the stored cruise_type is the fallback
      // and stays the key everywhere else. The attached PDF carries the long name.
      cruiseLabel(names, 'short') || booking.cruise_type || '—',
      booking.date,                    // date   — DD-MM-YYYY (stored format)
      reportingTime,                   // reporting time — from cruise_settings for this cruise
      boardingPoint,                   // boarding point — from ticket_settings
    ];

    const body = {
      bookingId: booking.id,
      pdfBase64: pdf.base64,
      fileName: pdf.filename,
      phoneNumbers: uniqueNumbers,
      bodyValues,
    };

    let data;
    try {
      const resp = await supabase.functions.invoke('send-ticket', { body });
      if (resp.error) {
        let msg = resp.error.message;
        try { const ctx = await resp.error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
        setTopError(msg || 'Could not send. Please try again.');
        setSending(false);
        return;
      }
      data = resp.data;
    } catch (err) {
      console.error('send-ticket invoke failed:', err);
      setTopError('Could not reach the server. Please try again.');
      setSending(false);
      return;
    }

    // Map the function's per-number results back to labelled recipients, then
    // append any invalid (not-sent) recipients.
    const byNumber = {};
    (data?.results || []).forEach((r) => { byNumber[r.phoneNumber] = r; });
    const sentRows = valid.map((r) => {
      const res = byNumber[r.norm.number] || {};
      return { label: r.label, number: r.norm.number, success: !!res.success, error: res.error };
    });
    const invalidRows = invalid.map((r) => ({ label: r.label, number: r.raw, success: false, error: r.norm.reason }));
    setResults([...sentRows, ...invalidRows]);
    setSending(false);
  };

  const anyValidSelected = chosen.some((r) => normalizeIndianMobile(r.raw).valid);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px 26px', width: '100%', maxWidth: 460, boxShadow: '0 12px 40px rgba(10,22,40,0.22)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, background: WA_GREEN, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
              <MessageCircle size={19} strokeWidth={1.75} aria-hidden="true" />
            </div>
            <div>
              <div style={{ fontSize: '0.98rem', fontWeight: 700, color: '#0A1628' }}>Send ticket via WhatsApp</div>
              <div style={{ fontSize: '0.74rem', color: 'var(--color-ink-muted)', marginTop: 1 }}>{booking.booking_name} · {booking.cruise_type}</div>
            </div>
          </div>
          <button onClick={onClose} disabled={sending} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--color-ink-muted)', cursor: sending ? 'not-allowed' : 'pointer', padding: 4, borderRadius: 6, opacity: sending ? 0.4 : 1, display: 'inline-flex' }}>
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>

        {topError && <div className="toast toast-error" style={{ marginBottom: 14 }}>{topError}</div>}

        {/* No recipients at all */}
        {available.length === 0 ? (
          <div className="toast toast-error" style={{ marginBottom: 4 }}>
            No phone number on this booking — nothing to send.
          </div>
        ) : results ? (
          /* ── Result screen ─────────────────────────────────────────────── */
          <div>
            {results.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 8, marginBottom: 8 }}>
                <span style={{ flexShrink: 0, marginTop: 1, color: r.success ? '#15803d' : 'var(--color-danger-strong)' }}>
                  {r.success ? <Check size={16} strokeWidth={2.25} /> : <AlertTriangle size={16} strokeWidth={2} />}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0A1628' }}>{r.label} · {r.number}</div>
                  <div style={{ fontSize: '0.78rem', color: r.success ? '#15803d' : 'var(--color-danger-strong)' }}>
                    {r.success ? 'Sent.' : (r.error || 'Failed.')}
                  </div>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setResults(null)} disabled={sending}>
                Send again
              </button>
              <button className="btn btn-navy" style={{ flex: 1 }} onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          /* ── Choose + send screen ──────────────────────────────────────── */
          <div>
            {hasChoice && (
              <>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-secondary)', margin: '0 0 10px', fontWeight: 600 }}>
                  This is a partner booking. Who should get the ticket?
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {[
                    { key: 'guest', label: 'Guest only' },
                    { key: 'partner', label: 'Partner only' },
                    { key: 'both', label: 'Both' },
                  ].map((opt) => {
                    const selected = choice === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => setChoice(opt.key)}
                        style={{
                          textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                          border: `1.5px solid ${selected ? WA_GREEN : 'var(--color-border-strong)'}`,
                          background: selected ? '#f0fdf4' : '#fff',
                          fontSize: '0.86rem', fontWeight: 700, color: '#0A1628',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Recipient preview with normalized numbers / invalid warnings */}
            {chosen.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {chosen.map((r) => {
                  const n = normalizeIndianMobile(r.raw);
                  return (
                    <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '7px 0', fontSize: '0.83rem' }}>
                      <span style={{ color: 'var(--color-ink-secondary)', fontWeight: 600 }}>{r.label}</span>
                      {n.valid
                        ? <span style={{ color: '#0A1628', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>+91 {n.number}</span>
                        : <span style={{ color: 'var(--color-danger-strong)', fontSize: '0.78rem', textAlign: 'right' }}>{n.reason}</span>}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              className="btn btn-whatsapp"
              style={{ width: '100%' }}
              onClick={handleSend}
              disabled={sending || !choice || !anyValidSelected}
            >
              {sending ? 'Sending…' : 'Send via WhatsApp'}
            </button>
            {!sending && choice && !anyValidSelected && (
              <p style={{ fontSize: '0.76rem', color: 'var(--color-danger-strong)', margin: '10px 0 0', textAlign: 'center' }}>
                No valid number to send to.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
